import { useEffect, useState } from "react";
import { Loader2, Download } from "lucide-react";
import { api, fileKind } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function useBlobUrl(loader, deps) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let u = null; let cancelled = false;
    setUrl(null); setError(null);
    loader().then((blob) => {
      if (cancelled) return;
      u = URL.createObjectURL(blob); setUrl(u);
    }).catch((e) => !cancelled && setError(e));
    return () => { cancelled = true; if (u) URL.revokeObjectURL(u); };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return { url, error };
}

export function PreviewBody({ file, url, error, textPreview, onDownload }) {
  const kind = fileKind(file);
  if (error) return error.message === "preview-not-allowed" ? <NoPreview onDownload={onDownload} /> : <p className="text-sm text-red-600 p-4">Impossible de charger l'aperçu.</p>;
  if (["word", "sheet", "slides", "text"].includes(kind)) {
    return textPreview ? (
      <div>
        {kind !== "text" && <p className="text-xs text-slate-500 mb-2">Aperçu du contenu texte extrait du document.</p>}
        <pre className="whitespace-pre-wrap text-[13px] leading-relaxed bg-white border rounded-md p-4 max-h-[65vh] overflow-auto font-mono" data-testid="preview-text">{textPreview}</pre>
      </div>
    ) : <NoPreview onDownload={onDownload} />;
  }
  if (!url) return <div className="h-64 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#1E3A8A]" /></div>;
  if (kind === "pdf") return <iframe title="Aperçu PDF" src={url} className="w-full h-[68vh] rounded-md border bg-white" data-testid="preview-pdf" />;
  if (kind === "image") return <img src={url} alt={file.name} className="max-h-[68vh] mx-auto rounded-md object-contain" data-testid="preview-image" />;
  if (kind === "video") return <video src={url} controls className="w-full max-h-[68vh] rounded-md bg-black" data-testid="preview-video" />;
  if (kind === "audio") return <audio src={url} controls className="w-full" data-testid="preview-audio" />;
  return <NoPreview onDownload={onDownload} />;
}

const NoPreview = ({ onDownload, reason = "Aperçu non disponible pour ce format." }) => (
  <div className="h-48 flex flex-col items-center justify-center text-center gap-3 border border-dashed rounded-md">
    <p className="text-sm text-slate-600">{reason}</p>
    {onDownload && <Button variant="outline" size="sm" onClick={onDownload} data-testid="preview-download-btn"><Download className="h-4 w-4 mr-2" />Télécharger</Button>}
  </div>
);

// Only server-approved renderable types become previews; anything else stays an inert download.
export const safePreviewBlob = (r) => {
  const type = (r.headers?.["content-type"] || "").split(";")[0].trim();
  if (!/^(application\/pdf|image\/(png|jpeg|gif|webp|bmp)|video\/(mp4|webm|quicktime)|audio\/(mpeg|wav|ogg|mp4))$/.test(type)) {
    throw new Error("preview-not-allowed");
  }
  return new Blob([r.data], { type });
};

export function FilePreview({ file, version, textPreview, onDownload }) {
  const tooBig = (file.size || 0) > 50 * 1024 * 1024;
  const needsBlob = !tooBig && ["pdf", "image", "video", "audio"].includes(fileKind(file));
  const { url, error } = useBlobUrl(
    () => (needsBlob ? api.get(`/files/${file.id}/content`, { params: { version }, responseType: "blob" }).then(safePreviewBlob) : Promise.resolve(new Blob())),
    [file.id, version, file.version]);
  if (tooBig) return <NoPreview onDownload={onDownload} reason="Fichier trop volumineux pour l'aperçu (plus de 50 Mo)." />;
  return <PreviewBody file={file} url={url} error={error} textPreview={textPreview} onDownload={onDownload} />;
}

export async function downloadFile(file, version) {
  if ((file.size || 0) > 50 * 1024 * 1024) {
    const { data } = await api.get(`/files/${file.id}/download-url`, { params: { version } });
    if (data.url) { window.location.href = data.url; return; }
  }
  const r = await api.get(`/files/${file.id}/content`, { params: { version, download: true }, responseType: "blob" });
  const u = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = u; a.download = file.name; a.click();
  setTimeout(() => URL.revokeObjectURL(u), 2000);
}
